import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  fetchPromotionPreview,
  finalizeSeason,
  clearStandingsError,
  clearTransitionSuccess,
} from '../../store/slices/standingsSlice';
import {
  fetchLeague,
  createSeason,
  fetchSeasons,
  fetchDivisionsForSeason,
} from '../../store/slices/leagueSlice';
import SeasonTransitionWizard from '../../components/admin/SeasonTransitionWizard';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import type { CreateSeasonRequest } from '@sulis-liiga/common';
import { SeasonStatus } from '@sulis-liiga/common';

/**
 * SeasonTransitionPage
 *
 * Admin page for managing season transitions. Provides a 4-step wizard:
 * Review (promotion preview) → Finalize → Create next season (optional) → Done.
 */
const SeasonTransitionPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId, seasonId } = useParams<{ leagueId: string; seasonId: string }>();

  const { currentLeague, seasons, divisions } = useSelector((state: RootState) => state.league);
  const { promotionPreview, transitionLoading, error } = useSelector(
    (state: RootState) => state.standings,
  );

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchSeasons(leagueId));
      // Load this season's divisions so the wizard can block finalization until
      // every division is completed.
      if (seasonId) {
        dispatch(fetchDivisionsForSeason({ leagueId, seasonId }));
      }
    }
  }, [dispatch, leagueId, seasonId, tenantResolved]);

  useEffect(() => {
    return () => {
      dispatch(clearStandingsError());
      dispatch(clearTransitionSuccess());
    };
  }, [dispatch]);

  const handleFetchPreview = () => {
    if (leagueId && seasonId) {
      dispatch(fetchPromotionPreview({ leagueId, seasonId }));
    }
  };

  // Unwrap so a rejected thunk throws: the wizard advances only on success and the
  // error banner surfaces the failure (e.g. the divisions-not-completed guard).
  const handleFinalize = async () => {
    if (leagueId && seasonId) {
      await dispatch(finalizeSeason({ leagueId, seasonId })).unwrap();
    }
  };

  // Returns the created season so the wizard can link to its management page.
  const handleCreateSeason = async (data: CreateSeasonRequest) => {
    if (!leagueId) return undefined;
    return await dispatch(createSeason({ leagueId, seasonData: data })).unwrap();
  };

  const handleManageSeason = (newSeasonId: string) => {
    navigate(`/leagues/${leagueId}/seasons/${newSeasonId}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/leagues" className="text-gray-400 hover:text-gray-500 text-sm">
              Leagues
            </Link>
          </li>
          <li className="text-gray-400">/</li>
          {leagueId && (
            <>
              <li>
                <Link
                  to={`/leagues/${leagueId}`}
                  className="text-gray-400 hover:text-gray-500 text-sm"
                >
                  {currentLeague?.name || 'League'}
                </Link>
              </li>
              <li className="text-gray-400">/</li>
            </>
          )}
          <li>
            <span className="text-gray-700 text-sm font-medium">Season Transition</span>
          </li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Season Transition</h1>
        <p className="mt-1 text-sm text-gray-500">
          Finalize the current season and optionally create the next one.
        </p>

        {/* Season-to-transition-from selector. Changing it re-scopes the whole
            wizard (preview, finalize, and the division/placement basis) to the
            chosen season, so an admin can transition any season — not only the
            league's current one. */}
        {seasons.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <label htmlFor="from-season" className="text-sm font-medium text-gray-700 shrink-0">
              Transition from season:
            </label>
            <select
              id="from-season"
              value={seasonId ?? ''}
              onChange={e => {
                const next = e.target.value;
                if (next && next !== seasonId) {
                  navigate(`/leagues/${leagueId}/seasons/${next}/transition`);
                }
              }}
              className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-1.5 bg-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              aria-label="Select the season to transition from"
            >
              {[...seasons]
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                .map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.status === SeasonStatus.ACTIVE ? ' (active)' : ''}
                    {s.status === SeasonStatus.COMPLETED ? ' (completed)' : ''}
                    {s.status === SeasonStatus.UPCOMING ? ' (upcoming)' : ''}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 rounded-md bg-danger-50 p-4 border border-danger-200">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      )}

      {/* Wizard */}
      {leagueId && seasonId ? (
        <SeasonTransitionWizard
          key={seasonId}
          leagueId={leagueId}
          seasonId={seasonId}
          existingSeasons={seasons}
          divisions={divisions}
          promotionPreview={promotionPreview}
          loading={transitionLoading}
          onFetchPreview={handleFetchPreview}
          onFinalize={handleFinalize}
          onCreateSeason={handleCreateSeason}
          onManageSeason={handleManageSeason}
        />
      ) : (
        <div className="text-center py-12 text-gray-500">League and season context required.</div>
      )}
    </div>
  );
};

export default SeasonTransitionPage;
